import { Component, computed, signal, OnInit, OnDestroy, effect, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService, Notification } from '../../../core/services/notifications/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Spinner } from '../spinner/spinner';
import { Router } from '@angular/router';

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.html',
  styleUrls: ['./notification-dropdown.scss'],
  standalone: true,
  imports: [CommonModule, MatTooltipModule, Spinner]
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  
  // Track which notification is being marked as read for animation
  markingAsRead: string | null = null;
  
  // Track image loading errors
  imageLoadError: { [key: string]: boolean } = {};

  @Output() viewAll = new EventEmitter<void>();
  @Output() notificationClicked = new EventEmitter<void>();
  // Use service signals
  allNotifications = computed(() => this.notificationService.notifications());
  unreadCount = computed(() => this.notificationService.unreadCount());
  isLoading = computed(() => this.notificationService.isLoading());
  isInitialized = computed(() => this.notificationService.isInitialized());
  
  // Filter to show only unread notifications (like in the image)
  notifications = computed(() => {
    const allNotifs = this.allNotifications();
    const unseen = allNotifs.filter(n => !n.isSeen);
    
    // Sort by creation date (newest first)
    const sorted = unseen.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
    
    // Limit to 5 notifications
    return sorted.slice(0, 5);
  });

  constructor(public notificationService: NotificationService) {
    // Set up effect to track notifications
    effect(() => {
      const notifs = this.notifications();
      console.log('NotificationDropdownComponent: Notifications:', notifs.length);
    });
  }

  ngOnInit(): void {
    // Listen for new notifications
    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        console.log('NotificationDropdownComponent: New notification received:', notification);
      });
  }

  ngOnDestroy(): void {
    // Clean up
    this.destroy$.next();
    this.destroy$.complete();
  }

  markAllAsSeen() {
    // Mark all unread notifications as read
    const unreadNotifications = this.notifications();
      this.notificationService.markAllAsSeen();
    
  }

  markAsSeen(notification: Notification) {
    // Set the marking state for animation
    this.markingAsRead = notification.id;
    
    // Add a small delay to show the animation before actually marking as read
    setTimeout(() => {
      this.notificationService.markAsSeen(notification.id);
      this.markingAsRead = null;
    }, 200);
  }

  togglePin(notification: Notification) {
    this.notificationService.togglePin(notification.id);
  }

  viewAllNotifications() {
    this.viewAll.emit();
    // The parent component (header) will handle closing the dropdown
  }

  /** Resolves display time (avoids empty row when `timestamp` missing but `createdAt` exists). */
  notificationTimeDisplay(notification: Notification): string {
    return this.notificationService.getNotificationTimeDisplay(notification);
  }

  // Helper method to get date label from timestamp
  getDateLabel(timestamp: string): string {
    if (timestamp.includes('Today')) return '';
    if (timestamp.includes('Yesterday')) return '';
    // For other dates, extract the date part before 'at'
    const parts = timestamp.split(' at ');
    return parts.length > 1 ? parts[0] + ' at ' : '';
  }

  // Helper method to get time value from timestamp
  getTimeValue(timestamp: string): string {
    const parts = timestamp.split(' at ');
    return parts.length > 1 ? parts[1] : timestamp;
  }

  // Get initials from name for avatar fallback
  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  }

  // Convert text to sentence case (first letter uppercase, rest lowercase)
  toSentenceCase(text: string): string;
  toSentenceCase(text: string | undefined): string;
  toSentenceCase(text: string | undefined): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }

  // Get avatar classes for styling
  getAvatarClasses(notificationId: string, hasAvatar: boolean): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'user-avatar': true,
      'has-avatar': hasAvatar,
    };

    if (!hasAvatar) {
      // Use notification ID to create consistent color assignment
      const hash = notificationId.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      const paletteIndex = (Math.abs(hash) % 6) + 1;
      classes['avatar-placeholder'] = true;
      classes[`avatar-palette-${paletteIndex}`] = true;
    }

    return classes;
  }

  // Handle image loading errors
  onImageError(notificationId: string): void {
    this.imageLoadError[notificationId] = true;
  }

  // Handle successful image loading
  onImageLoad(notificationId: string): void {
    this.imageLoadError[notificationId] = false;
  }

  onNotificationClick(notification: Notification, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    // Mark as seen first
    this.markAsSeen(notification);

    // Get route based on notification object details
    const route = this.notificationService.getNotificationRoute(notification);
    
    if (route) {
      // Get fragment for scrolling to specific update/reply
      const fragment = this.notificationService.getNotificationFragment(notification);
      
      // Navigate with fragment if available
      if (fragment) {
        this.router.navigate(route, { fragment });
      } else {
        this.router.navigate(route);
      }
    }

    // Emit event to notify parent (header) to close the dropdown
    this.notificationClicked.emit();
  }
}
